import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProductContext } from "../context/AppContext";
import Header from "./Header";
import { Link } from "react-router-dom";
import { all_routes } from "./router/all_routes";
import { Card, Row, Col, Container, Badge, Image, Button, Table, Accordion } from "react-bootstrap";
import { 
    FaChevronLeft, 
    FaUser, 
    FaBook, 
    FaHistory, 
    FaEdit, 
    FaClock,
    FaCheckCircle,
    FaTimesCircle,
    FaEye,
    FaCalendarAlt,
    FaFileAlt,
    FaInfoCircle,
    FaSort,
    FaFilter,FaMapMarkerAlt,
    FaIdCard,
    FaPhone,
    FaEnvelope,
    FaLink,
    FaFileMedical,
    FaImage
} from 'react-icons/fa';

const UpdateHistory = () => {
    const { GetstudentData } = useContext(ProductContext);
    const navigate = useNavigate();
    const [auditData, setAuditData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('All');
    const [sortBy, setSortBy] = useState('newest');
    const [expandedItems, setExpandedItems] = useState([]);

    useEffect(() => {
        fetchAuditData();
    }, []);

    const fetchAuditData = async () => {
        try {
            setLoading(true);
            const authtoken = localStorage.getItem("token");
            const response = await fetch(`https://api.mirai.healthcare/api/student/audit`, {
                headers: {
                    "x-auth": authtoken
                }
            });
            const result = await response.json();
            if (response.ok) {
                setAuditData(result.message || []);
            }
        } catch (error) {
            console.error("Error fetching audit data:", error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved':
                return 'success';
            case 'Pending':
                return 'warning';
            case 'Rejected':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Approved':
                return <FaCheckCircle className="me-1" />;
            case 'Pending':
                return <FaClock className="me-1" />;
            case 'Rejected':
                return <FaTimesCircle className="me-1" />;
            default:
                return <FaInfoCircle className="me-1" />;
        }
    };

    const getFieldIcon = (field) => {
        const fieldIcons = {
            'student_full_name': <FaUser className="text-primary" />,
            'date_of_birth': <FaCalendarAlt className="text-info" />,
            'aadhar_no': <FaIdCard className="text-warning" />,
            'contact_address': <FaMapMarkerAlt className="text-danger" />,
            'father_full_name': <FaUser className="text-primary" />,
            'father_mobile_no': <FaPhone className="text-success" />,
            'father_email': <FaEnvelope className="text-info" />,
            'father_aadhar_no': <FaIdCard className="text-warning" />,
            'mother_full_name': <FaUser className="text-primary" />,
            'mother_mobile_no': <FaPhone className="text-success" />,
            'mother_email': <FaEnvelope className="text-info" />,
            'mother_aadhar_no': <FaIdCard className="text-warning" />,
            'guardian_name': <FaUser className="text-primary" />,
            'guardian_mobile_no': <FaPhone className="text-success" />,
            'guardian_email': <FaEnvelope className="text-info" />,
            'guardian_aadhar_no': <FaIdCard className="text-warning" />,
            'guardian_relation': <FaLink className="text-secondary" />,
            'character_certificate': <FaFileAlt className="text-primary" />,
            'medical_certificate': <FaFileMedical className="text-danger" />,
            'cast_certificate': <FaFileAlt className="text-warning" />,
            'upload_photo': <FaImage className="text-success" />,
            'father_photo': <FaImage className="text-info" />,
            'mother_photo_upload': <FaImage className="text-info" />
        };
        return fieldIcons[field] || <FaEdit className="text-secondary" />;
    };

    const formatFieldName = (field) => {
        return field
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const renderValue = (value, field) => {
        if (value === null || value === "null" || value === undefined || value === '') {
            return <span className="text-muted fst-italic">Not provided</span>;
        }
        
        if (typeof value === 'object' && value?.link) {
            return (
                <div className="text-center">
                    <Image 
                        src={value.link} 
                        alt="Document" 
                        thumbnail 
                        style={{ maxWidth: '120px', maxHeight: '120px' }} 
                        fluid 
                        className="mb-2"
                    />
                    <br />
                    <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => window.open(value.link, '_blank')}
                    >
                        <FaEye className="me-1" /> View
                    </Button>
                </div>
            );
        }
        
        // Format date fields
        if (field === 'date_of_birth' && value) {
            return new Date(value).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
        
        // Truncate long text for mobile
        if (typeof value === 'string' && value.length > 50) {
            return (
                <span title={value}>
                    {value.substring(0, 50)}...
                </span>
            );
        }
        
        return <span>{value}</span>;
    };

    // Filter and sort data
    const filteredAndSortedData = auditData
        .filter(item => filterStatus === 'All' || item.status === filterStatus)
        .sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.updated_at) - new Date(a.updated_at);
                case 'oldest':
                    return new Date(a.updated_at) - new Date(b.updated_at);
                case 'status':
                    return a.status.localeCompare(b.status);
                default:
                    return 0;
            }
        });

    const toggleExpand = (index) => {
        setExpandedItems(prev => 
            prev.includes(index) 
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const statusCounts = {
        All: auditData.length,
        Pending: auditData.filter(item => item.status === 'Pending').length,
        Approved: auditData.filter(item => item.status === 'Approved').length,
        Rejected: auditData.filter(item => item.status === 'Rejected').length,
    };

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content content-two">
                    <Header />
                    <Container fluid className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <h5 className="text-muted">Loading Update History...</h5>
                        </div>
                    </Container>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content content-two">
                <Header />
                <Container fluid>
                    {/* Header Section */}
                        <Row className="align-items-center justify-content-between mt-3 my-2 pb-3">
                        <Col xs={12} lg={8}>
                            <div className="d-flex align-items-center mb-2">
                                {/* <Button 
                                    variant="outline-primary" 
                                    className="me-3" 
                                    onClick={() => navigate(-1)}
                                    size="sm"
                                >
                                    <FaChevronLeft />
                                </Button> */}
                                <div>
                                    <h2
              className="page-title fw-bold mb-0"
              style={{ letterSpacing: "0.5px" }}
            >
                               
                                        Student Update History
                                    </h2>
                                    <p className="text-muted mb-0">
                                        Track all your update requests and their status
                                    </p>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} lg={4} className="text-lg-end mt-2 mt-lg-0">
                            <Link to="/AapproveStudentInfo" className="btn btn-primary">
                                <FaEdit className="me-2" /> 
                                Update Details
                            </Link>
                        </Col>
                    </Row>

                    {/* Student Summary Card */}
                    <Card className="mb-4 shadow-sm">
                        <Card.Body className="p-3">
                            <Row className="align-items-center">
                                <Col xs={12} md={8}>
                                    <Row className="g-3">
                                        <Col xs={12} sm={6} md={4}>
                                            <div className="text-center">
                                                <div className="bg-primary bg-opacity-10 rounded p-3">
                                                    <FaHistory className="text-primary mb-2" size={24} />
                                                    <h5 className="mb-1">{auditData.length}</h5>
                                                    <small className="text-muted">Total Requests</small>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6} md={4}>
                                            <div className="text-center">
                                                <div className="bg-warning bg-opacity-10 rounded p-3">
                                                    <FaClock className="text-warning mb-2" size={24} />
                                                    <h5 className="mb-1">{statusCounts.Pending}</h5>
                                                    <small className="text-muted">Pending</small>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6} md={4}>
                                            <div className="text-center">
                                                <div className="bg-success bg-opacity-10 rounded p-3">
                                                    <FaCheckCircle className="text-success mb-2" size={24} />
                                                    <h5 className="mb-1">{statusCounts.Approved}</h5>
                                                    <small className="text-muted">Approved</small>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col xs={12} md={4} className="text-center text-md-end">
                                    <div className="border-start ps-3 d-none d-md-block">
                                        <h5 className="mb-1">{GetstudentData?.student_full_name || 'N/A'}</h5>
                                        <p className="text-muted mb-1">
                                            {GetstudentData?.cls_sec?.cls?.Classes || 'N/A'} {GetstudentData?.cls_sec?.section?.section || ''}
                                        </p>
                                        <Badge bg="info">
                                            Roll No: {GetstudentData?.roll_num || 'N/A'}
                                        </Badge>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Filters and Controls */}
                    <Card className="mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-center">
                                <Col xs={12} md={6}>
                                    <div className="d-flex align-items-center">
                                        <FaFilter className="text-muted me-2" />
                                        <strong className="me-3">Filter by Status:</strong>
                                        <div className="btn-group btn-group-sm" role="group">
                                            {['All', 'Pending', 'Approved', 'Rejected'].map(status => (
                                                <Button
                                                    key={status}
                                                    variant={filterStatus === status ? 'primary' : 'outline-primary'}
                                                    onClick={() => setFilterStatus(status)}
                                                    size="sm"
                                                >
                                                    {status} {status !== 'All' && `(${statusCounts[status]})`}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={12} md={6}>
                                    <div className="d-flex align-items-center justify-content-md-end">
                                        <FaSort className="text-muted me-2" />
                                        <strong className="me-3">Sort by:</strong>
                                        <select 
                                            className="form-select form-select-sm w-auto"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                        >
                                            <option value="newest">Newest First</option>
                                            <option value="oldest">Oldest First</option>
                                            <option value="status">Status</option>
                                        </select>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Update History List */}
                    {filteredAndSortedData.length > 0 ? (
                        <Row>
                            <Col xs={12}>
                                <div className="timeline">
                                    {filteredAndSortedData.map((item, index) => (
                                        <Card key={index} className="mb-3 shadow-sm">
                                            <Card.Header 
                                                className="bg-light cursor-pointer"
                                                onClick={() => toggleExpand(index)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Row className="align-items-center">
                                                    <Col xs={1} className="text-center">
                                                        {getFieldIcon(item.updated_field)}
                                                    </Col>
                                                    <Col xs={7} md={8}>
                                                        <h6 className="mb-0">
                                                            {formatFieldName(item.updated_field)}
                                                        </h6>
                                                        <small className="text-muted">
                                                            Updated on: {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'N/A'}
                                                        </small>
                                                    </Col>
                                                    <Col xs={4} md={3} className="text-end">
                                                        <Badge bg={getStatusColor(item.status)} className="fs-6">
                                                            {getStatusIcon(item.status)}
                                                            {item.status}
                                                        </Badge>
                                                    </Col>
                                                </Row>
                                            </Card.Header>
                                            
                                            {expandedItems.includes(index) && (
                                                <Card.Body>
                                                    <Row className="g-3">
                                                        {/* Old Value */}
                                                        <Col xs={12} md={6}>
                                                            <Card className="h-100 border">
                                                                <Card.Header className="bg-light py-2">
                                                                    <h6 className="mb-0 text-muted">
                                                                        <FaHistory className="me-1" />
                                                                        Previous Value
                                                                    </h6>
                                                                </Card.Header>
                                                                <Card.Body className="d-flex align-items-center justify-content-center min-h-100">
                                                                    {renderValue(item.old_value, item.updated_field)}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>

                                                        {/* New Value */}
                                                        <Col xs={12} md={6}>
                                                            <Card className="h-100 border-success">
                                                                <Card.Header className="bg-success bg-opacity-10 py-2">
                                                                    <h6 className="mb-0 text-success">
                                                                        <FaEdit className="me-1" />
                                                                        Requested Value
                                                                    </h6>
                                                                </Card.Header>
                                                                <Card.Body className="d-flex align-items-center justify-content-center min-h-100">
                                                                    {renderValue(item.new_value, item.updated_field)}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>

                                                        {/* Additional Information */}
                                                        <Col xs={12}>
                                                            <Row className="g-3">
                                                                {/* User Reason */}
                                                                {item.user_reason && (
                                                                    <Col xs={12} md={6}>
                                                                        <Card className="border-info">
                                                                            <Card.Header className="bg-info bg-opacity-10 py-2">
                                                                                <h6 className="mb-0 text-info">
                                                                                    <FaInfoCircle className="me-1" />
                                                                                    Your Reason
                                                                                </h6>
                                                                            </Card.Header>
                                                                            <Card.Body>
                                                                                <p className="mb-0">{item.user_reason}</p>
                                                                            </Card.Body>
                                                                        </Card>
                                                                    </Col>
                                                                )}

                                                                {/* Admin Remark */}
                                                                {item.admin_reason && (
                                                                    <Col xs={12} md={6}>
                                                                        <Card className="border-warning">
                                                                            <Card.Header className="bg-warning bg-opacity-10 py-2">
                                                                                <h6 className="mb-0 text-warning">
                                                                                    <FaInfoCircle className="me-1" />
                                                                                    Admin Remark
                                                                                </h6>
                                                                            </Card.Header>
                                                                            <Card.Body>
                                                                                <p className="mb-0">{item.admin_reason}</p>
                                                                            </Card.Body>
                                                                        </Card>
                                                                    </Col>
                                                                )}

                                                                {/* Optional Document */}
                                                                {item.optional_document?.link && (
                                                                    <Col xs={12}>
                                                                        <Card className="border-secondary">
                                                                            <Card.Header className="bg-secondary bg-opacity-10 py-2">
                                                                                <h6 className="mb-0 text-secondary">
                                                                                    <FaFileAlt className="me-1" />
                                                                                    Supporting Document
                                                                                </h6>
                                                                            </Card.Header>
                                                                            <Card.Body className="text-center">
                                                                                <Image 
                                                                                    src={item.optional_document.link} 
                                                                                    alt="Supporting Document" 
                                                                                    thumbnail 
                                                                                    style={{ maxWidth: '200px' }} 
                                                                                    fluid 
                                                                                    className="mb-2"
                                                                                />
                                                                                <br />
                                                                                <Button 
                                                                                    variant="outline-secondary" 
                                                                                    size="sm"
                                                                                    onClick={() => window.open(item.optional_document.link, '_blank')}
                                                                                >
                                                                                    <FaEye className="me-1" /> View Full Document
                                                                                </Button>
                                                                            </Card.Body>
                                                                        </Card>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            )}
                                        </Card>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    ) : (
                        <Card className="text-center py-5">
                            <Card.Body>
                                <FaHistory size={64} className="text-muted mb-3 opacity-50" />
                                <h4 className="text-muted mb-3">No Update Requests Found</h4>
                                <p className="text-muted mb-4">
                                    {filterStatus !== 'All' 
                                        ? `No ${filterStatus.toLowerCase()} update requests found.`
                                        : "You haven't made any update requests yet."
                                    }
                                </p>
                                <Link to="/student/update-details" className="btn btn-primary btn-lg">
                                    <FaEdit className="me-2" /> 
                                    Make Your First Update
                                </Link>
                            </Card.Body>
                        </Card>
                    )}
                </Container>

                <style jsx>{`
                    .min-h-100 {
                        min-height: 100px;
                    }
                    .cursor-pointer {
                        cursor: pointer;
                    }
                    .timeline {
                        position: relative;
                    }
                    .timeline::before {
                        content: '';
                        position: absolute;
                        left: 30px;
                        top: 0;
                        bottom: 0;
                        width: 2px;
                        background: #e9ecef;
                    }
                    @media (max-width: 768px) {
                        .timeline::before {
                            left: 15px;
                        }
                        .btn-group {
                            flex-wrap: wrap;
                        }
                        .btn-group .btn {
                            margin-bottom: 5px;
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default UpdateHistory;